import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

interface MenuItem {
  name: string;
  description?: string;
  allergens?: string[];
  vegetarian?: boolean;
  vegan?: boolean;
  vegan_option?: boolean;
  price?: number;
  rating?: number;
}

interface MenuSection {
  id?: string;
  title: string;
  items: MenuItem[];
}

interface SectionBlock {
  type: 'section';
  title: string;
}

interface ItemBlock {
  type: 'item';
  item: MenuItem;
}

type ColumnBlock = SectionBlock | ItemBlock;

interface MenuPage {
  leftColumn: ColumnBlock[];
  rightColumn: ColumnBlock[];
}





@Component({
  selector: 'app-menu-layout',
  templateUrl: './menu-layout.component.html',
  styleUrls: ['./menu-layout.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MenuLayoutComponent implements OnInit {
  @ViewChild('measureHost', { static: true }) measureHost!: ElementRef<HTMLDivElement>;
  @ViewChildren('pageRef') pageRefs!: QueryList<ElementRef<HTMLDivElement>>;

  restaurantName = 'The Cock';
  partnerName = 'foodbud';
  menuTitle = 'RESTAURANT MENU';
  restaurantLogo = ''
  qrCode = ''

  allergensDietary: any[] = []

  loader: boolean = false;

  selectedFields = {
    description: true,
    allergens: true,
    vegetarian: true,
    vegan: true,
    price: true,
    ratings: false
  };

  // Landscape page is shorter in height than portrait,
  // so capacity is a little smaller.
  footerReservedHeight = 0;

  sections: MenuSection[] = [];

  pages: MenuPage[] = [];

  allergenMsg: string = 'We use food containing allergens in our kitchen, so please let us knowif you or anyone else on your table has an allergy or special dietary requirement.';

  constructor(
    private dialogRef: MatDialogRef<MenuLayoutComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) { }

  ngOnInit(): void {
    this.sections = this.dialogData.dish_type;
    this.restaurantLogo = this.dialogData.logo
    this.restaurantName = this.dialogData.name
    this.qrCode = this.dialogData.qrcode_url
    this.qrCode = this.qrCode.replace('qr/tableqr_', 'qr/qr_codes/tableqr_')
    this.allergensDietary = this.dialogData.restaurant_allergens.map(item => ({
      name: item,
      ...this.getShortCode(item)
    }));

    this.buildPages();
  }

  onOptionsChange(): void {
    this.buildPages();
  }

  buildPages(): void {
    this.footerReservedHeight = 0;

    const firstPassPages = this.paginatePages();
    this.footerReservedHeight = this.getMaxFooterHeight(firstPassPages);

    const finalPages = this.paginatePages();
    this.pages = finalPages;
  }
  private paginatePages(): MenuPage[] {
    const pages: MenuPage[] = [];

    let leftColumn: ColumnBlock[] = [];
    let rightColumn: ColumnBlock[] = [];
    let currentColumn: 'left' | 'right' = 'left';

    const pushPage = () => {
      if (leftColumn.length || rightColumn.length) {
        pages.push({
          leftColumn: [...leftColumn],
          rightColumn: [...rightColumn]
        });
      }
      leftColumn = [];
      rightColumn = [];
      currentColumn = 'left';
    };

    const canFit = (left: ColumnBlock[], right: ColumnBlock[]): boolean => {
      return this.pageFits(left, right);
    };

    const placeBlocks = (
      blocks: ColumnBlock[],
      preferred: 'left' | 'right'
    ): 'left' | 'right' => {
      if (preferred === 'left') {
        if (canFit([...leftColumn, ...blocks], rightColumn)) {
          leftColumn.push(...blocks);
          return 'left';
        }

        if (canFit(leftColumn, [...rightColumn, ...blocks])) {
          rightColumn.push(...blocks);
          return 'right';
        }

        pushPage();
        leftColumn.push(...blocks);
        return 'left';
      }

      if (canFit(leftColumn, [...rightColumn, ...blocks])) {
        rightColumn.push(...blocks);
        return 'right';
      }

      if (canFit([...leftColumn, ...blocks], rightColumn)) {
        leftColumn.push(...blocks);
        return 'left';
      }

      pushPage();
      leftColumn.push(...blocks);
      return 'left';
    };

    for (const section of this.sections) {
      if (!section.items?.length) {
        continue;
      }

      currentColumn = placeBlocks(
        [
          { type: 'section', title: section.title },
          { type: 'item', item: section.items[0] }
        ],
        currentColumn
      );

      for (let i = 1; i < section.items.length; i++) {
        const itemBlock: ColumnBlock = { type: 'item', item: section.items[i] };

        if (currentColumn === 'left') {
          if (canFit([...leftColumn, itemBlock], rightColumn)) {
            leftColumn.push(itemBlock);
          } else {
            currentColumn = placeBlocks(
              [
                { type: 'section', title: section.title },
                itemBlock
              ],
              'right'
            );
          }
        } else {
          if (canFit(leftColumn, [...rightColumn, itemBlock])) {
            rightColumn.push(itemBlock);
          } else {
            currentColumn = placeBlocks(
              [
                { type: 'section', title: section.title },
                itemBlock
              ],
              'left'
            );
          }
        }
      }
    }

    pushPage();
    return pages;
  }

  getItemUnits(item: MenuItem): number {
    let units = 2;

    if (this.selectedFields.description && item.description) {
      units += Math.ceil(item.description.length / 75);
    }

    if (this.selectedFields.allergens && item.allergens?.length) {
      units += Math.ceil(item.allergens.join(', ').length / 60);
    }

    if (this.selectedFields.ratings && item.rating !== undefined && item.rating !== null) {
      units += 1;
    }

    return units;
  }

  formatPrice(price?: any): string {
    if (price === undefined || price === null) {
      return '';
    }
    return `€${price}`;
  }

  trackByItem(index: number, item: MenuItem): string {
    return `${item.name}-${index}`;
  }

  readonly colors: string[] = [
    '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444',
    '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#14B8A6',
    '#6366F1', '#22C55E', '#EAB308', '#A855F7', '#F43F5E',
    '#0EA5E9', '#65A30D', '#D97706', '#7C3AED', '#059669'
  ];

  shortCode: Record<string, { code: string; color: string }> = {};

  private predefinedCodes: Record<string, string> = {
    'vegan option available': 'VGO',
    'vegan': 'VG',
    'contains allergens': 'AG',
    'vegetarian option available': 'VO',
    'vegetarian': 'V',
    'contains nuts': 'N',
    'nuts': 'N'
  };

  getShortCode(name: string) {
    if (!name) return { code: '', color: '' };

    const key = name.trim().toLowerCase();

    if (this.shortCode[key]) {
      return this.shortCode[key];
    }

    let code = this.predefinedCodes[key];

    // if predefined code already used by another name, make it unique too
    if (code && this.isCodeUsed(code, key)) {
      code = this.makeUniqueCode(name, code);
    }

    if (!code) {
      code = this.makeUniqueCode(name);
    }

    const color = this.colors[Object.keys(this.shortCode).length % this.colors.length];

    this.shortCode[key] = { code, color };
    return this.shortCode[key];
  }

  private isCodeUsed(code: string, currentKey?: string): boolean {
    return Object.entries(this.shortCode).some(
      ([key, value]) => key !== currentKey && value.code === code
    );
  }

  private makeUniqueCode(name: string, preferredCode?: string): string {
    const cleanName = name.trim().replace(/[^a-zA-Z0-9 ]/g, '');
    const words = cleanName.split(/\s+/).filter(Boolean);

    const candidates: string[] = [];

    // 1. predefined/preferred first
    if (preferredCode) {
      candidates.push(preferredCode.toLowerCase());
    }

    // 2. initials for multi-word names
    if (words.length > 1) {
      candidates.push(words.map(w => w[0]).join('').toLowerCase()); // e.g. Vegan Option Available => VOA
      candidates.push(words.map(w => w[0]).join('').slice(0, 3).toLowerCase()); // VGO style if needed
    }

    // 3. single word candidates
    if (words.length === 1) {
      const word = words[0].toLowerCase();
      candidates.push(word.slice(0, 1)); // a
      candidates.push(word.slice(0, 2)); // ab
      candidates.push(word.slice(0, 3)); // abc
      candidates.push(word.slice(0, 4)); // abcd
    }

    // 4. extra candidates for all names
    const joined = words.join('').toLowerCase();
    candidates.push(joined.slice(0, 2));
    candidates.push(joined.slice(0, 3));
    candidates.push(joined.slice(0, 4));

    // remove empty/duplicate candidates
    const uniqueCandidates = [...new Set(candidates.filter(Boolean))];

    for (const code of uniqueCandidates) {
      if (!this.isCodeUsed(code)) {
        return code;
      }
    }

    // 5. final fallback with number
    let base = uniqueCandidates[uniqueCandidates.length - 1] || 'X';
    let count = 2;
    while (this.isCodeUsed(`${base}${count}`)) {
      count++;
    }

    return `${base}${count}`;
  }


  getTagDetails(name: string) {
    return this.allergensDietary.find(
      x => x.name.trim().toLowerCase() === name.trim().toLowerCase()
    ) || { name, code: name, color: '#000' };
  }


  getVeganOption(arr: any) {
    if (arr.length == 0) return false
    let i = arr.findIndex((x: any) => x == "Vegan")
    return i == -1 ? false : true
  }

  async downloadPdf(): Promise<void> {
    this.loader = true;
    const pageElements = this.pageRefs?.toArray();

    if (!pageElements || !pageElements.length) {
      return;
    }

    if ((document as any).fonts?.ready) {
      await (document as any).fonts.ready;
    }

    await new Promise(resolve => setTimeout(resolve, 100));

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const pdfWidth = 297;
    const pdfHeight = 210;

    for (let i = 0; i < pageElements.length; i++) {
      const pageElement = pageElements[i].nativeElement;

      const canvas = await html2canvas(pageElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.98);

      if (i > 0) {
        pdf.addPage('a4', 'landscape');
      }

      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
    }
    this.loader = false;
    pdf.save('menu.pdf');
  }




  private readonly FIT_SAFETY_PX = 6;

  private pageFits(leftBlocks: ColumnBlock[], rightBlocks: ColumnBlock[]): boolean {
    const host = this.measureHost.nativeElement;
    host.innerHTML = '';

    const page = this.createMeasurePage(leftBlocks, rightBlocks);
    host.appendChild(page);

    const columns = page.querySelector('.columns') as HTMLElement;
    const footer = page.querySelector('.bottom-info-box') as HTMLElement;

    const columnsRect = columns.getBoundingClientRect();
    const footerRect = footer.getBoundingClientRect();

    const fits = columnsRect.bottom <= (footerRect.top - this.FIT_SAFETY_PX);

    host.innerHTML = '';
    return fits;
  }

  private createMeasurePage(leftBlocks: ColumnBlock[], rightBlocks: ColumnBlock[]): HTMLElement {
    const page = document.createElement('div');
    page.className = 'pdf-page';

    page.innerHTML = `
    <div class="page-inner">
      <img class="page-border-image" src="assets/img/boder_pdf_menu.svg" alt="" />

      <div class="page-header">
        <div class="menu-heading-left">
          <div class="menu-title-left">${this.escapeHtml(this.menuTitle || '')}</div>
        </div>

        <div class="brand-center">
          <div class="logo-group">
            <div class="logo-box">LOGO</div>
            <div class="restaurant-brand">
              <div class="restaurant-name">${this.escapeHtml(this.restaurantName || '')}</div>
              <div class="partner-name">Hemingford Grey</div>
            </div>
          </div>

          <div class="separator-line"></div>

          <div class="partner-brand">
            <div class="partner-logo-box">fb</div>
            <div class="partner-brand-name">${this.escapeHtml(this.partnerName || '')}</div>
          </div>
        </div>

        <div class="qr-block">
          <div class="qr-box">QR</div>
          <div class="qr-text">
            Scan the QR code for a personalised menu<br />
            based on your dietary preferences
          </div>
        </div>
      </div>

      <div class="header-line with-diamond"></div>

      <div class="page-content">
        <div class="columns">
          <div class="column left-measure"></div>
          <div class="column right-measure"></div>
        </div>
      </div>

      ${this.getBottomInfoHtml([...leftBlocks, ...rightBlocks], this.footerReservedHeight)}
    </div>
  `;

    const leftCol = page.querySelector('.left-measure') as HTMLElement;
    const rightCol = page.querySelector('.right-measure') as HTMLElement;

    leftBlocks.forEach(block => leftCol.appendChild(this.createBlockElement(block)));
    rightBlocks.forEach(block => rightCol.appendChild(this.createBlockElement(block)));

    return page;
  }

  private createBlockElement(block: ColumnBlock): HTMLElement {
    const wrapper = document.createElement('div');

    if (block.type === 'section') {
      wrapper.className = 'section-title';
      wrapper.textContent = block.title;
      return wrapper;
    }

    const item = block.item;
    const badges: string[] = [];

    if (this.selectedFields.vegetarian && item.vegetarian) {
      badges.push(`<span class="tag veg">(v)</span>`);
    }

    if (this.selectedFields.vegan && item.vegan) {
      badges.push(`<span class="tag vegan">(vg)</span>`);
    }

    wrapper.className = 'menu-item';
    wrapper.innerHTML = `
    <div class="item-row">
      <div class="item-title-wrap">
        <span class="item-name">${this.escapeHtml(item.name || '')}</span>
        ${badges.length
        ? `<span class="item-badges">${badges.join('')}</span>`
        : ''
      }
      </div>

      ${this.selectedFields.price && item.price !== undefined
        ? `<div class="item-price">${this.escapeHtml(this.formatPrice(item.price))}</div>`
        : ''
      }
    </div>

    ${this.selectedFields.description && item.description
        ? `<div class="item-description">${this.escapeHtml(item.description)}</div>`
        : ''
      }

    ${this.selectedFields.allergens && item.allergens?.length
        ? `<div class="item-allergens"><span class="label">Allergens:</span> ${this.escapeHtml(item.allergens.join(', '))}</div>`
        : ''
      }

    ${this.selectedFields.ratings && item.rating !== undefined
        ? `<div class="item-rating"><span class="label">Ratings:</span> ${this.escapeHtml(String(item.rating))}/5</div>`
        : ''
      }
  `;

    return wrapper;
  }

  private getBottomInfoHtml(blocks: ColumnBlock[], fixedHeight = 0): string {
    const allergens = this.getUniqueAllergens(blocks);

    const allergenLegend = allergens.length
      ? allergens
        .map(
          a => `<div><span class="legend-code allergen">${this.escapeHtml(a)}</span></div>`
        )
        .join('')
      : '';

    const style = fixedHeight
      ? ` style="height:${fixedHeight}px; min-height:${fixedHeight}px;"`
      : '';


    let rightContent = '';
    if (this.selectedFields.vegetarian) {
      rightContent += `<div><span class="legend-code veg">(v)</span> - Vegetarian</div>`
    }
    if (this.selectedFields.vegan) {
      rightContent += `<div><span class="legend-code vegan">(vg)</span> - Vegan</div>`
      rightContent += `<div><span class="legend-code veganopt">(vgo)</span> - Vegan option available</div>`
    }
    if (this.selectedFields.allergens && this.allergensDietary.length) {
      this.allergensDietary.forEach(item => {
        rightContent += `<div><span class="legend-code" style="color: ${item.color}">${item.code}</span> - ${item.name}</div>`
      })
    }

    return `
    <div class="bottom-info-box"${style}>
      <div class="bottom-info-left">
        We use food containing allergens in our kitchen, so please let us know if you or anyone else on your table has an allergy or special dietary requirement.
      </div>

      <div class="bottom-info-right">
        <div class="legend-grid">
          ${rightContent}
          ${allergenLegend}
        </div>
      </div>
    </div>
  `;
  }

  private getUniqueAllergens(blocks: ColumnBlock[]): string[] {
    const set = new Set<string>();

    for (const block of blocks) {
      if (block.type === 'item' && block.item.allergens?.length) {
        block.item.allergens.forEach(a => set.add(a));
      }
    }

    return Array.from(set);
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }



  private getMaxFooterHeight(pages: MenuPage[]): number {
    let maxHeight = 0;

    for (const page of pages) {
      const h = this.measureFooterHeight([
        ...page.leftColumn,
        ...page.rightColumn
      ]);

      if (h > maxHeight) {
        maxHeight = h;
      }
    }

    return Math.ceil(maxHeight);
  }

  private measureFooterHeight(blocks: ColumnBlock[]): number {
    const host = this.measureHost.nativeElement;
    host.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'pdf-page';
    wrapper.innerHTML = `
    <div class="page-inner">
      ${this.getBottomInfoHtml(blocks)}
    </div>
  `;

    host.appendChild(wrapper);

    const footer = wrapper.querySelector('.bottom-info-box') as HTMLElement;
    const height = footer.getBoundingClientRect().height;

    host.innerHTML = '';
    return height;
  }
}